import * as React from 'react';
import { useMemo, useState } from 'react';

import { translate } from '~l10n/client';
import { Badge } from '~shared/components/Badge';
import { SmallButton } from '~shared/components/buttons';
import { Link } from '~shared/components/Link';
import ToggleSwitch from '~shared/components/ToggleSwitch/ToggleSwitch';
import { TooltipV2 } from '~shared/components/TooltipV2';
import { FileDrop } from '~teacher/components/FileDrop/FileDrop';
import { FileDropConfig } from '~teacher/components/FileDrop/hooks/useFileDropConfig';
import { InformationIcon } from '~teacher/components/QuizEditor/GenerateQuestionDialog/InformationIcon';
import { LearnMoreIcon } from '~teacher/components/QuizEditor/GenerateQuestionDialog/LearnMoreIcon';
import { EightyPercentQuotaInlineMessage } from '~teacher/components/QuotaInlineMessage/EightyPercentQuotaInlineMessage';
import { HundredPercentQuotaInlineMessage } from '~teacher/components/QuotaInlineMessage/HundredPercentQuotaInlineMessage';
import { useAiQuotaWarning } from '~teacher/hooks/useAiQuotaWarning';
import { proCheck } from '~teacher/util/proCheck';

import { DispatchExtractQuestionsAction } from './useExtractQuestionsReducer';

interface ExtractQuestionsFormProps extends FileDropConfig {
  includeExplanation: boolean;
  dispatch: DispatchExtractQuestionsAction;
  onExtract: () => void;
}

export function ExtractQuestionsForm({
  tasks,
  addFileTasks,
  removeFileTasks,
  retryFileTasks,
  includeExplanation,
  dispatch,
  onExtract,
  ...fileDropConfig
}: ExtractQuestionsFormProps) {
  const isUploadButtonDisabled = useMemo(
    () => tasks.some((task) => task.status !== 'success'),
    [tasks]
  );
  const {
    level: quotaLevel,
    resetDate,
    percentUsed,
  } = useAiQuotaWarning('Import');
  const [warningDismissed, setWarningDismissed] = useState(false);

  return (
    <>
      <div className="pb-4">
        <div className="flex">
          <div className="flex flex-shrink flex-col">
            <div className="flex items-center gap-2">
              <Link
                indicatorProps={{ isInset: false, notOffset: true }}
                href={
                  'https://help.socrative.com/en/articles/8732134-socrative-ai-question-generation'
                }
                target="_blank"
                rel="noopener noreferrer"
                className="no-underline"
              >
                <Badge textTransform="capitalize" tone="ai">
                  {translate('AI Assisted')}
                  <LearnMoreIcon />
                </Badge>
              </Link>
            </div>
            <div className="font-display text-md leading-normal text-blue-900 font-700 flex items-center gap-2 mt-2">
              {translate('Extract Questions from Document')}
              {proCheck.hasPaidPlan() ? (
                <Badge textTransform="uppercase" tone="beta">
                  {translate('Beta')}
                </Badge>
              ) : (
                <Badge className="font-sans" tone="pro">
                  Pro
                </Badge>
              )}
            </div>
            <div className="text-sans text-base leading-normal text-blue-900 mt-1">
              {translate(
                "Upload a file, and we'll find and extract the questions in it."
              )}
            </div>
          </div>
        </div>
      </div>

      <div
        className="flex flex-col gap-4 pt-5"
        style={{
          boxShadow: '0px 1px 0px 0px #E7EDF0 inset',
        }}
      >
        <FileDrop
          label={<FileDrop.Label label={translate('Upload Document')} />}
          fileTasks={tasks}
          addFileTasks={addFileTasks}
          removeFileTasks={removeFileTasks}
          retryFileTasks={retryFileTasks}
          {...fileDropConfig}
        />
      </div>
      <div className="flex justify-between items-center gap-2 py-5 pr-5 shadow-inset-border-t">
        <div className="flex flex-row items-center gap-2">
          <label
            className="text-base text-blue-900"
            htmlFor="generate-explanations-toggle"
            id="generate-explanations-label"
          >
            {translate('Generate explanations')}{' '}
          </label>
          <TooltipV2
            mode="click"
            contentWidth={260}
            label={translate(
              'Only for multiple-choice and true / false. Explains the correct answer to students after they submit a response. You can choose to show explanations from your Launch settings.'
            )}
          >
            <InformationIcon />
          </TooltipV2>
          <span id="generate-explanations-description" className="sr-only">
            {translate(
              'Only for multiple-choice and true / false. Explains the correct answer to students after they submit a response. You can choose to show explanations from your Launch settings.'
            )}
          </span>
        </div>
        <ToggleSwitch
          id="generate-explanations-toggle"
          labelledBy="generate-explanations-label"
          describedBy="generate-explanations-description"
          size="small"
          on={includeExplanation}
          onChange={(value) => {
            dispatch({
              type: 'setIncludeExplanation',
              payload: { newIncludeExplanation: value },
            });
          }}
        />
      </div>
      <div className="flex justify-end items-center h-24 gap-4 pt-5 shadow-inset-border-t">
        {quotaLevel === 'warning' && !warningDismissed ? (
          <EightyPercentQuotaInlineMessage
            bucket="Import"
            upgradeHref="#pricing"
            onClose={() => setWarningDismissed(true)}
            percentUsed={percentUsed}
          />
        ) : null}
        {quotaLevel === 'exceeded' ? (
          <HundredPercentQuotaInlineMessage
            bucket="Import"
            upgradeHref="#pricing"
            limitResetDate={resetDate}
            percentUsed={percentUsed}
          />
        ) : null}
        <div>
          <SmallButton
            onClick={onExtract}
            isDisabled={isUploadButtonDisabled || quotaLevel === 'exceeded'}
            appearance="primary"
          >
            {translate('Add')}
          </SmallButton>
        </div>
      </div>
    </>
  );
}
